import { useState, useEffect } from 'react';
import api from '../api/client';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Filter } from 'lucide-react';

const ACTION_LABELS = {
  login: 'Login', user_created: 'User Created', user_updated: 'User Updated', password_reset: 'Password Reset',
  template_created: 'Template Created', template_updated: 'Template Updated', template_published: 'Template Published',
  template_archived: 'Template Archived', template_duplicated: 'Template Duplicated', template_deleted: 'Template Deleted',
  template_exported: 'Template Exported', template_imported: 'Template Imported',
  run_started: 'Run Started', run_completed: 'Run Completed', run_duplicated: 'Run Duplicated', run_abandoned: 'Run Abandoned',
  settings_updated: 'Settings Updated', logo_uploaded: 'Logo Uploaded', logo_deleted: 'Logo Deleted',
};

const ACTION_COLORS = {
  login: 'bg-blue-50 text-blue-700 border-blue-200', user_created: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  user_updated: 'bg-amber-50 text-amber-700 border-amber-200', password_reset: 'bg-red-50 text-red-700 border-red-200',
  template_created: 'bg-emerald-50 text-emerald-700 border-emerald-200', template_updated: 'bg-amber-50 text-amber-700 border-amber-200',
  template_published: 'bg-emerald-50 text-emerald-700 border-emerald-200', template_archived: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  template_duplicated: 'bg-indigo-50 text-indigo-700 border-indigo-200', template_deleted: 'bg-red-50 text-red-700 border-red-200',
  template_exported: 'bg-blue-50 text-blue-700 border-blue-200', template_imported: 'bg-purple-50 text-purple-700 border-purple-200',
  run_started: 'bg-emerald-50 text-emerald-700 border-emerald-200', run_completed: 'bg-blue-50 text-blue-700 border-blue-200',
  run_duplicated: 'bg-indigo-50 text-indigo-700 border-indigo-200', run_abandoned: 'bg-zinc-100 text-zinc-500 border-zinc-200',
  settings_updated: 'bg-amber-50 text-amber-700 border-amber-200', logo_uploaded: 'bg-blue-50 text-blue-700 border-blue-200',
  logo_deleted: 'bg-red-50 text-red-700 border-red-200',
};

const RESOURCE_TYPES = ['', 'auth', 'user', 'template', 'run', 'settings'];

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [resourceFilter, setResourceFilter] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 25;

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ skip: String(page * pageSize), limit: String(pageSize) });
    if (resourceFilter) params.set('resource_type', resourceFilter);
    api.get(`/audit-logs?${params}`).then(r => {
      setLogs(r.data.logs); setTotal(r.data.total); setLoading(false);
    }).catch(() => setLoading(false));
  }, [resourceFilter, page]);

  const totalPages = Math.ceil(total / pageSize);
  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-6" data-testid="audit-log-page">
      <p className="text-sm text-zinc-500 mb-5">Track all system activity</p>

      <div className="flex items-center gap-3 mb-4">
        <Filter size={13} className="text-zinc-400" />
        <div className="flex gap-2">
          {RESOURCE_TYPES.map(rt => (
            <Button key={rt} variant={resourceFilter === rt ? 'default' : 'outline'} size="sm" onClick={() => { setResourceFilter(rt); setPage(0); }}
              className={`rounded-sm text-xs ${resourceFilter === rt ? 'bg-zinc-900 text-white' : ''}`} data-testid={`filter-${rt || 'all'}`}>
              {rt ? rt.charAt(0).toUpperCase() + rt.slice(1) : 'All'}
            </Button>
          ))}
        </div>
        <span className="text-[10px] font-semibold text-zinc-400 ml-auto">{total} events</span>
      </div>

      {loading ? <div className="text-sm text-zinc-400 py-8">Loading...</div> : logs.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm px-4 py-12 text-center text-sm text-zinc-400">No audit events found</div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4 w-40">Time</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4 w-36">User</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4 w-44">Action</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Resource</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log, i) => (
                <TableRow key={i} data-testid={`audit-row-${i}`}>
                  <TableCell className="px-4 py-2.5 text-xs text-zinc-500">{formatTime(log.timestamp)}</TableCell>
                  <TableCell className="px-4 py-2.5">
                    <div className="font-medium text-zinc-800 text-xs">{log.actor_name}</div>
                    <div className="text-[10px] text-zinc-400">{log.actor_email}</div>
                  </TableCell>
                  <TableCell className="px-4 py-2.5">
                    <Badge variant="outline" className={`rounded-sm text-[10px] font-semibold ${ACTION_COLORS[log.action] || 'bg-zinc-100 text-zinc-600 border-zinc-200'}`}>
                      {ACTION_LABELS[log.action] || log.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-4 py-2.5 text-zinc-600 text-xs">{log.resource_name || log.resource_type || '-'}</TableCell>
                  <TableCell className="px-4 py-2.5 text-zinc-400 text-xs truncate max-w-[200px]">
                    {typeof log.details === 'string' ? log.details : log.details ? JSON.stringify(log.details) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="rounded-sm text-xs">Previous</Button>
          <span className="text-xs text-zinc-400">Page {page + 1} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="rounded-sm text-xs">Next</Button>
        </div>
      )}
    </div>
  );
}
